/**
 * Inline input component for creating files and folders
 */

export interface InlineInputConfig {
  type: 'file' | 'folder';
  parentPath: string;
  depth: number;
  onConfirm: (name: string) => Promise<void>;
  onCancel: () => void;
}

/**
 * Create an inline input element for file/folder creation
 */
export function createInlineInput(config: InlineInputConfig): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'tree-inline-input';
  wrapper.dataset.type = config.type;

  // Calculate indentation
  const indent = config.depth * 16;
  wrapper.style.paddingLeft = `${12 + indent}px`;

  // Icon
  const icon = document.createElement('span');
  icon.className = 'tree-inline-icon';

  if (config.type === 'folder') {
    icon.innerHTML = `<svg class="tree-folder-icon" xmlns="http://www.w3.org/2000/svg" height="14" viewBox="0 -960 960 960" width="14" fill="currentColor"><path d="M160-160q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h240l80 80h320q33 0 56.5 23.5T880-640v400q0 33-23.5 56.5T800-160H160Z"/></svg>`;
  } else {
    icon.innerHTML = `<svg class="tree-file-icon" xmlns="http://www.w3.org/2000/svg" height="14" viewBox="0 -960 960 960" width="14" fill="currentColor"><path d="M320-240h320v-80H320v80Zm0-160h320v-80H320v80ZM240-80q-33 0-56.5-23.5T160-160v-640q0-33 23.5-56.5T240-880h320l240 240v480q0 33-23.5 56.5T720-80H240Z"/></svg>`;
  }

  // Input field
  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'tree-inline-input-field';
  input.placeholder = config.type === 'folder' ? 'folder name' : 'file name';

  wrapper.appendChild(icon);
  wrapper.appendChild(input);

  let isConfirming = false;

  // Handle confirm
  const confirm = async () => {
    if (isConfirming) return;

    const name = input.value.trim();
    if (!name) {
      config.onCancel();
      return;
    }

    isConfirming = true;
    input.disabled = true;

    try {
      await config.onConfirm(name);
    } catch (err) {
      console.error('Failed to create:', err);
      isConfirming = false;
      input.disabled = false;
      input.focus();
    }
  };

  // Handle keyboard events
  input.addEventListener('keydown', async (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      await confirm();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      config.onCancel();
    }
  });

  // Handle blur (clicking away) - only cancel if empty
  input.addEventListener('blur', () => {
    // Small delay to allow Enter key to process first
    setTimeout(() => {
      if (!isConfirming) {
        const name = input.value.trim();
        if (name) {
          confirm();
        } else {
          config.onCancel();
        }
      }
    }, 100);
  });

  // Focus after adding to DOM
  requestAnimationFrame(() => {
    input.focus();
  });

  return wrapper;
}

/**
 * Create an inline rename input for a file or folder
 */
export function createInlineRename(
  currentName: string,
  depth: number,
  type: 'file' | 'folder',
  onConfirm: (newName: string) => Promise<void>,
  onCancel: () => void
): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'tree-inline-input tree-inline-rename';
  wrapper.dataset.type = type;

  // Calculate indentation
  const indent = depth * 16;
  wrapper.style.paddingLeft = `${12 + indent}px`;

  // Icon
  const icon = document.createElement('span');
  icon.className = 'tree-inline-icon';

  if (type === 'folder') {
    icon.innerHTML = `<svg class="tree-folder-icon" xmlns="http://www.w3.org/2000/svg" height="14" viewBox="0 -960 960 960" width="14" fill="currentColor"><path d="M160-160q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h240l80 80h320q33 0 56.5 23.5T880-640v400q0 33-23.5 56.5T800-160H160Z"/></svg>`;
  } else {
    icon.innerHTML = `<svg class="tree-file-icon" xmlns="http://www.w3.org/2000/svg" height="14" viewBox="0 -960 960 960" width="14" fill="currentColor"><path d="M320-240h320v-80H320v80Zm0-160h320v-80H320v80ZM240-80q-33 0-56.5-23.5T160-160v-640q0-33 23.5-56.5T240-880h320l240 240v480q0 33-23.5 56.5T720-80H240Z"/></svg>`;
  }

  // Input field
  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'tree-inline-input-field';
  input.value = currentName;

  wrapper.appendChild(icon);
  wrapper.appendChild(input);

  let isConfirming = false;

  // Handle confirm
  const confirm = async () => {
    if (isConfirming) return;

    const newName = input.value.trim();
    if (!newName || newName === currentName) {
      onCancel();
      return;
    }

    isConfirming = true;
    input.disabled = true;

    try {
      await onConfirm(newName);
    } catch (err) {
      console.error('Failed to rename:', err);
      isConfirming = false;
      input.disabled = false;
      input.focus();
    }
  };

  // Handle keyboard events
  input.addEventListener('keydown', async (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      await confirm();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  });

  // Handle blur
  input.addEventListener('blur', () => {
    setTimeout(() => {
      if (!isConfirming) {
        const newName = input.value.trim();
        if (newName && newName !== currentName) {
          confirm();
        } else {
          onCancel();
        }
      }
    }, 100);
  });

  // Focus and select after adding to DOM
  requestAnimationFrame(() => {
    input.focus();
    input.select();
  });

  return wrapper;
}
